<template>
  <div
    class="plan-card"
    :class="{ featured: isFeatured }"
  >
    <div class="card-glow-line" :class="{ 'glow-featured': isFeatured }"></div>

    <div v-if="isFeatured" class="popular-badge">
      {{ $t('pricing.most_popular') }}
    </div>

    <div class="plan-card-inner">
      <h3 class="plan-name">{{ plan.displayName || plan.name }}</h3>

      <div v-if="plan.sale" class="sale-flag">{{ plan.sale.label || ('−' + plan.sale.percentOff + '%') }}</div>

      <div class="plan-price">
        <span v-if="plan.sale" class="price-original">{{ formatPrice(plan.price) }}</span>
        <span class="price-amount">{{ formatPrice(plan.sale ? plan.sale.salePrice : plan.price) }}</span>
        <span class="price-interval" v-if="plan.price > 0">/ {{ plan.billingInterval === 'year' ? $t('pricing.per_year') : $t('pricing.per_month') }}</span>
        <span class="price-interval" v-else>{{ $t('pricing.free_forever') }}</span>
      </div>
      <p v-if="plan.sale" class="sale-note">
        {{ $t('pricing.sale_note', { months: plan.sale.durationMonths, price: formatPrice(plan.price) }) }}
      </p>
      <!-- Yearly view: the "what it really costs" line sells the discount. -->
      <p v-if="plan.billingInterval === 'year' && plan.monthlyEquiv" class="yearly-note">
        {{ $t('pricing.per_month_equiv', { price: formatPrice(plan.monthlyEquiv) }) }}
        <span v-if="plan.savePct > 0" class="yearly-save">{{ $t('pricing.save_pct', { pct: plan.savePct }) }}</span>
      </p>

      <p class="plan-royalties">
        <span class="royalties-label">{{ $t('pricing.royalties') }}:</span>
        <span class="royalties-value">{{ plan.keepRoyalties }}</span>
      </p>

      <ul class="plan-features">
        <li v-for="(feature, fi) in plan.features" :key="fi" class="feature-item">
          <span class="feature-check">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span>{{ featureText(feature) }}</span>
        </li>
      </ul>

      <button
        v-if="showButton"
        class="plan-cta"
        :class="{ 'cta-featured': isFeatured }"
        :disabled="loading"
        @click="$emit('subscribe')"
      >
        <span v-if="loading" class="btn-spinner"></span>
        {{ loading ? $t('pricing.processing') : $t('pricing.choose_plan', { name: plan.displayName || plan.name }) }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps({
  plan: {
    type: Object as PropType<{
      name: string
      displayName?: string | null
      price: number
      billingInterval?: 'month' | 'year'
      monthlyEquiv?: number | null
      savePct?: number
      keepRoyalties: string
      features: string[]
      sale?: { percentOff: number; durationMonths: number; label?: string | null; salePrice: number } | null
    }>,
    required: true
  },
  isFeatured: {
    type: Boolean,
    default: false
  },
  showButton: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

defineEmits(['subscribe'])

const { formatPrice } = usePricingTable()
const { t, te } = useI18n()

// Feature entries may be i18n keys (`plan_features.*`, the canonical
// defaults) or tenant-authored plain text (the subscriptions editor) —
// translate known keys, render everything else verbatim.
const featureText = (feature: string) => (te(feature) ? t(feature) : feature)
</script>

<style scoped>
.plan-card {
  position: relative;
  background: var(--surface-color);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  border-color: rgba(0, 242, 255, 0.2);
  box-shadow: 0 0 40px rgba(0, 242, 255, 0.05);
  transform: translateY(-4px);
}

.plan-card.featured {
  border-color: rgba(0, 242, 255, 0.25);
  box-shadow: 0 0 60px rgba(0, 242, 255, 0.08);
  transform: scale(1.03);
}

.plan-card.featured:hover {
  transform: scale(1.03) translateY(-4px);
  box-shadow: 0 0 80px rgba(0, 242, 255, 0.12);
}

.card-glow-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--primary), transparent);
  opacity: 0.3;
}

.card-glow-line.glow-featured {
  height: 2px;
  opacity: 0.7;
  background: linear-gradient(to right, transparent, var(--primary), var(--secondary), transparent);
}

.popular-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: rgba(0, 242, 255, 0.1);
  border: 1px solid rgba(0, 242, 255, 0.25);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--primary);
  z-index: 2;
}

.plan-card-inner {
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.plan-name {
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 16px;
}

.plan-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
}

.price-amount {
  font-family: var(--font-heading);
  font-size: 3rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.price-interval {
  font-size: 0.95rem;
  color: var(--text-dim);
  font-weight: 500;
}

.sale-flag {
  display: inline-block;
  align-self: flex-start;
  margin-bottom: 8px;
  padding: 3px 10px;
  border-radius: 9999px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: #000;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.price-original {
  font-size: 1.1rem;
  color: var(--text-dim);
  text-decoration: line-through;
  align-self: flex-end;
  margin-bottom: 6px;
}

.sale-note {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-bottom: 16px;
  margin-top: -8px;
}

.yearly-note {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-bottom: 16px;
  margin-top: -8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.yearly-save {
  font-size: 0.7rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgb(var(--primary-channels) / 0.15);
  color: var(--primary);
  white-space: nowrap;
}

.plan-royalties {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.royalties-label {
  font-weight: 600;
  color: var(--text-muted);
}

.royalties-value {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
}

.plan-features {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
  flex: 1;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.4;
}

.feature-check {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 242, 255, 0.1);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.plan-cta {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--radius-lg);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-muted);
}

.plan-cta:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.25);
}

.plan-cta.cta-featured {
  background: rgba(0, 242, 255, 0.1);
  border-color: var(--primary);
  color: var(--primary);
}

.plan-cta.cta-featured:hover:not(:disabled) {
  background: var(--primary);
  color: #000;
  box-shadow: 0 0 20px rgba(0, 242, 255, 0.4);
}

.plan-cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1024px) {
  .plan-card.featured {
    transform: none;
  }

  .plan-card.featured:hover {
    transform: translateY(-4px);
  }
}

@media (max-width: 768px) {
  .plan-card-inner {
    padding: 32px 24px;
  }

  .price-amount {
    font-size: 2.5rem;
  }
}
</style>
